import { computed, Injectable, signal } from '@angular/core';
import { ErrorPayload } from '../../shared/models/system';

/**
 * Service for managing application error state.
 * Uses signals for reactive error state management.
 */
@Injectable({ providedIn: 'root' })
export class ErrorService {
  private readonly _serverError = signal<ErrorPayload | null>(null);
  readonly serverError = this._serverError.asReadonly();

  private readonly _clientError = signal<Error | null>(null);
  readonly clientError = this._clientError.asReadonly();

  /**
   * Computed signal that provides the client error message.
   * Returns 'No Internet Connection' if offline, otherwise the error message.
   */
  readonly clientErrorMessage = computed<string>(() => {
    if (!navigator.onLine) {
      return 'No Internet Connection';
    }
    const error = this._clientError();
    if (error) {
      return error.message ?? error.toString();
    }
    return '';
  });

  /**
   * Computed signal that provides the server error message.
   * Joins multiple error messages with line breaks.
   */
  readonly serverErrorMessage = computed<string>(() => {
    const error = this._serverError();
    if (error) {
      return error.errorMessages.join('<br/>');
    }
    return '';
  });

  /**
   * Computed signal indicating if there is an active server error.
   */
  readonly hasServerError = computed(() => this._serverError() !== null);

  /**
   * Computed signal indicating if there is an active client error.
   */
  readonly hasClientError = computed(() => this._clientError() !== null || !navigator.onLine);

  /**
   * Sets the server error state.
   * @param error - The error payload or null to clear
   */
  setServerError(error: ErrorPayload | null): void {
    this._serverError.set(error);
  }

  /**
   * Sets the client error state.
   * @param error - The error or null to clear
   */
  setClientError(error: Error | null): void {
    this._clientError.set(error);
  }

  /**
   * Clears all error states.
   */
  clearErrors(): void {
    this._serverError.set(null);
    this._clientError.set(null);
  }

  /**
   * Clears only the server error state.
   */
  clearServerError(): void {
    this._serverError.set(null);
  }

  /**
   * Clears only the client error state.
   */
  clearClientError(): void {
    this._clientError.set(null);
  }
}
